import React, { useCallback, useContext } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';

import { ReactComponent as EditIcon } from '@pkg/components/Sections/Profile/icons/edit.svg';
import { ReactComponent as ExpandIcon } from '@pkg/components/Sections/Profile/icons/expand.svg';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { setLiquidIdEditStage, updateYatPageEditStates } from '@app/store/slices';
import { LiquidIDEditStage } from '@app/store/slices/yat-page/types';

import { ExpandButton, TriggerButtonWrapper } from './styles';

interface Props {
    editMode?: boolean;
    isHovering?: boolean;
}

const TriggerButton = ({ editMode, isHovering }: Props) => {
    const dispatch = useAppDispatch();
    const theme = useContext(ThemeContext);
    const liquidIDEditStage = useAppSelector((state) => state.yatPage.liquidIDEditStage);
    const liquidIDExpanded = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);
    const navigate = useNavigate();

    const updateEditStage = useCallback(
        (newStage: LiquidIDEditStage) => {
            if (editMode && liquidIDEditStage !== newStage) {
                dispatch(setLiquidIdEditStage(newStage));
                dispatch(updateYatPageEditStates({ editingLiquidID: newStage === LiquidIDEditStage.Selection }));
            } else {
                navigate({ search: '?lid=1' });
            }
        },
        [dispatch, editMode, liquidIDEditStage, navigate],
    );

    const showIcon = !liquidIDExpanded && !editingLiquidID && (editMode || isHovering);

    return (
        <TriggerButtonWrapper>
            <AnimatePresence>
                {showIcon ? (
                    <ExpandButton
                        key="collapsed"
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: 1,
                        }}
                        exit={{
                            opacity: 0,
                            transition: { delay: 0, duration: 0 },
                        }}
                        transition={{
                            duration: theme.transitions.speeds.fast,
                            ease: theme.transitions.eases.on,
                        }}
                        onClick={() => updateEditStage(LiquidIDEditStage.Selection)}
                    >
                        {editMode ? <EditIcon /> : <ExpandIcon />}
                    </ExpandButton>
                ) : null}
            </AnimatePresence>
        </TriggerButtonWrapper>
    );
};

export default TriggerButton;
